/**
 * HSL (hue, saturation, lightness) color-space structure
 */
export interface HSL {
    h: number;
    s: number;
    l: number;
}

/**
 * HSV (hue, saturation, value) color-space structure
 */
export interface HSV {
    h: number;
    s: number;
    v: number;
}

/**
 * RGB (red, green, blue) color-space structure
 */
export interface RGB {
    r: number;
    g: number;
    b: number;
}

function componentToHex(c) {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
}

/**
 * Converts RGB color structure into string hex RGB representation
 *
 * @param color: RGB color as structure
 */
export function rgb2hex(color: RGB) {
    return '#' + componentToHex(color.r) + componentToHex(color.g) + componentToHex(color.b);
}

function hue2rgb(p, q, t) {
    if (t < 0) {
        t += 1;
    }

    if (t > 1) {
        t -= 1;
    }

    if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
        return q;
    }
    if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
}

/**
 * HSL to RGB color conversion
 *
 * @param param0: color in HSL color space
 */
export function hsl2rgb({ h: h, s: s, l: l }: HSL): RGB {

    h /= 360;
    s /= 100;
    l /= 100;

    let r; let g; let b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
}

/**
 * RGB to HSL color conversion
 *
 * @param param0: color in RGB color space
 */
export function rgb2hsl({ r: r, g: g, b: b }: RGB): HSL {
  r /= 255;
  g /= 255;
  b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h: number;
    let s: number;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }

        h /= 6;
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * HSL to HSV color conversion
 *
 * @param param0: color in HSL color space
 */
export function hsl2hsv({ h: h, s: s, l: l }: HSL): HSV {
    s /= 100;
    l /= 100;
    let sv; let v;

    if (s === 0) {
        return {
            h,
            s: 0,
            v: l * 100
        };
    }

    if (l === 0) {
        return {
            h,
            s: 0,
            v: 0
        };
    }

    l *= 2;
    s *= (l <= 1) ? l : 2 - l;
    v = (l + s) / 2;
    sv = (2 * s) / (l + s);
    return {
        h,
        s: sv * 100,
        v: v * 100
    };
}

/**
 * HSV to HSL color conversion
 *
 * @param param0: color in HSV color space
 */
export function hsv2hsl({ h: h, s: s, v: v }: HSV): HSL {
    s /= 100;
    v /= 100;
    let sl: number;
    let l: number;

    if (s === 0) {
        return {
            h,
            s: 0,
            l: v * 100
        };
    }

    if (v === 0) {
        return {
            h,
            s: 0,
            l: 0
        };
    }

    l = (2 - s) * v;
    sl = s * v;
    sl /= (l <= 1) ? l : 2 - l;
    l /= 2;
    return {
        h,
        s: sl * 100,
        l: l * 100
    };
}

/**
 * Parses hex rgb color representation to RGB structure
 *
 * @param param0: RGB color in a hex string fromat
 */
export function hex2rgb(hex: string): RGB {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}
